import React, { useEffect, useRef, useState } from "react";
import PhoneInput, { CountryData } from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { normalizeWhatsapp } from "@/hooks/useContactConflictCheck";

/**
 * Input de teléfono único para todos los formularios del admin.
 *
 * Existe por cuatro cosas que `react-phone-input-2` suelto no resuelve, todas verificadas contra los
 * datos reales del EAV 369 (13.237 números):
 *
 * 1. **El código de país no se puede borrar** (`countryCodeEditable`). Se cambia por la bandera, que
 *    es lo que la persona esperaba. Un número sin prefijo —o con medio prefijo— llega hasta el login:
 *    el WhatsApp es la clave con la que el cliente entra.
 *
 * 2. **Sólo el prefijo cuenta como vacío para el formulario, pero NUNCA para lo que se muestra.**
 *    Con el prefijo fijo el input nunca queda en blanco, así que si ese `"54"` se reportara tal cual,
 *    el formulario lo tomaría como un teléfono cargado: la regla "e-mail o WhatsApp" del alta daba el
 *    WhatsApp por completado sin que hubiera ninguno, y `createUser` llegaba a generar el e-mail
 *    sintético `54@wppxcons.com`.
 *
 *    Por eso hay **dos valores distintos**: el del formulario (vacío si sólo hay prefijo) y el que se
 *    muestra (nunca baja del prefijo). Mezclarlos **mata el input**, y no es una teoría: pasarle
 *    `value=""` a la librería hace `updateFormattedNumber("")` → `formattedNumber: ""`, o sea el
 *    campo sin el `+54`; y `handleInput` con `countryCodeEditable: false` arranca con
 *    `if (text.slice(0, "+54".length) !== "+54") return`, así que desde ese estado **ninguna tecla
 *    entra nunca más**. Reproducido en jsdom antes de arreglarlo.
 *
 * 3. **El valor que entra se normaliza** (ver `toDisplayValue`). Sin esto, 144 filas guardadas con el
 *    9 (`549…`) se mostraban **con un dígito menos** —la máscara argentina admite 12 dígitos— y ese
 *    dígito se perdía en cuanto el operador tocaba el campo. Y 855 filas guardadas en formato local
 *    (`011…`, `351…`, `381…`) mostraban bandera de **Portugal, Serbia o EE.UU.**, porque la librería
 *    adivina el país por los primeros dígitos.
 *
 * 4. **Sin agrupar los dígitos** (`masks`). La máscara argentina de la librería asume 2 dígitos de
 *    código de área, así que agrupaba mal todo el interior: `+54 (35) 17894512`. Los códigos de área
 *    van de 2 a 4 dígitos y ninguna máscara fija los cubre; sin agrupar nunca queda mal.
 */

/** Sólo cambia lo que se MUESTRA. El dato guardado cambia si el operador edita y guarda. */
const AR_LOCAL_MAX_DIGITS = 10;

export const toDisplayValue = (raw?: string, country?: string): string => {
  // El 0 de salida no existe en formato internacional: `0351…` y `05491…` son la misma línea.
  const withoutTrunk = String(raw ?? "")
    .replace(/\D/g, "")
    .replace(/^0+/, "");
  if (!withoutTrunk) return "";

  // La misma normalización del backend (`normalizeWhatsappNumber`): 549→54, 5415→5411. Se reusa el
  // helper del chequeo de conflictos para no tener una cuarta copia de la regla dando vueltas.
  const digits = normalizeWhatsapp(withoutTrunk);

  // Formato local argentino ⇒ se le pone el código de país. El tope de 10 dígitos es lo que
  // distingue "le falta el 54" de "es de otro país": un `593987794947` (Ecuador) se deja como está y
  // la librería lo muestra completo con su bandera, en vez de volverlo un argentino imposible.
  if (
    country === "ar" &&
    !digits.startsWith("54") &&
    digits.length <= AR_LOCAL_MAX_DIGITS
  ) {
    return `54${digits}`;
  }

  return digits;
};

type Props = {
  /** Lo que maneja el Form: dígitos con código de país. */
  value?: string;
  onChange?: (value: string) => void;
  /** País inicial. En los formularios de dirección viene del país de la dirección. */
  country?: string;
  disabled?: boolean;
  onBlur?: () => void;
  inputProps?: Record<string, unknown>;
  placeholder?: string;
  containerStyle?: React.CSSProperties;
};

export const PhoneNumberInput = ({
  value,
  onChange,
  country = "ar",
  disabled,
  onBlur,
  inputProps,
  placeholder,
  containerStyle,
}: Props) => {
  /**
   * El país lo maneja el componente, no el prop: cuando el operador borra el número el valor que se
   * reporta es `""`, y si el país saliera del prop la bandera volvería sola al default, perdiendo el
   * país que acababa de elegir.
   */
  const [selectedCountry, setSelectedCountry] = useState(country);

  /**
   * Lo que se MUESTRA, separado de lo que ve el formulario. Nunca baja del código de país: es lo que
   * mantiene vivo al input (ver punto 2 del comentario de arriba).
   */
  const [display, setDisplay] = useState(() => toDisplayValue(value, country));

  /** Último código de país conocido, para poder volver a él al limpiar el campo. */
  const dialCodeRef = useRef("");

  // El prop manda cuando cambia desde afuera: en los formularios de dirección el país llega después
  // del fetch, así que el primer render es con el default.
  useEffect(() => {
    if (country) setSelectedCountry(country);
  }, [country]);

  // Sincronización con el valor de afuera (el prefill del form, o un fetch que llega tarde). Un
  // valor externo vacío NO se propaga al display: eso es lo que dejaba el campo sin prefijo.
  useEffect(() => {
    const incoming = toDisplayValue(value, selectedCountry);
    if (incoming && incoming !== display) setDisplay(incoming);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  return (
    <PhoneInput
      country={selectedCountry}
      value={display}
      disabled={disabled}
      placeholder={placeholder}
      countryCodeEditable={false}
      masks={{ ar: ".........." }}
      /**
       * Argentina primera, y después los países de la región. El desplegable es alfabético, así que
       * **Argelia es el tercero de la lista** (Afghanistan, Albania, Algeria) y un click o un scroll
       * de más en la bandera cae justo ahí: el número queda guardado con `+213` en vez de `+54` y la
       * persona pierde el login por WhatsApp, sin ningún error visible. 12.040 de los 13.237 números
       * del padrón son argentinos.
       */
      preferredCountries={["ar", "uy", "cl", "br", "py", "bo"]}
      containerStyle={containerStyle}
      inputStyle={{ width: "100%" }}
      onBlur={onBlur}
      inputProps={inputProps}
      /**
       * Seleccionar todo y borrar: la librería descarta el cambio entero —el texto resultante no
       * empieza con el prefijo— y no pasa NADA, que se siente como un input roto. Acá se hace lo que
       * el operador quiere: se va el número, queda el prefijo.
       *
       * Va como prop de arriba y no dentro de `inputProps` a propósito: la librería llama a
       * `props.onKeyDown` desde su propio handler, así que de esta forma no se le pisa el manejo de
       * Enter (`inputProps` se aplica DESPUÉS de sus handlers y lo reemplazaría).
       */
      onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key !== "Backspace" && event.key !== "Delete") return;

        const input = event.currentTarget;
        const selectedAll =
          input.selectionStart === 0 && input.selectionEnd === input.value.length;
        if (!selectedAll || !input.value) return;

        const dialCode =
          dialCodeRef.current || input.value.replace(/^\+/, "").split(/[^0-9]/)[0] || "";
        if (!dialCode) return;

        event.preventDefault();
        dialCodeRef.current = dialCode;
        setDisplay(dialCode);
        onChange?.("");
      }}
      onChange={(next, data: CountryData | {}) => {
        const dialCode = (data as CountryData)?.dialCode ?? "";
        if (dialCode) dialCodeRef.current = dialCode;
        if ((data as CountryData)?.countryCode) {
          setSelectedCountry((data as CountryData).countryCode);
        }
        const digits = String(next ?? "").replace(/\D/g, "");
        // El display nunca baja del prefijo; el vacío es sólo para el formulario.
        setDisplay(digits || dialCode);
        onChange?.(digits && digits !== dialCode ? digits : "");
      }}
    />
  );
};
